import { useEffect } from 'react';

/**
 * Sets CSS custom properties for font families on the document root.
 * The caller decides which CSS variables map to which fonts.
 *
 * Why font config can't be shared from this package:
 * next/font uses a SWC compiler transform at build time — it statically
 * analyzes Inter() / Roboto_Mono() calls, downloads font files, and inlines
 * optimized CSS. This only works when arguments are literal values the
 * compiler can read at parse time. Imported variables (e.g. Inter(sharedConfig))
 * are opaque references the compiler can't resolve across module boundaries,
 * causing "Font loader values must be explicitly written literals" errors.
 * So each app must duplicate the font config as inline literals in _app.tsx.
 * This hook shares the runtime part: setting CSS custom properties after
 * the fonts are loaded.
 *
 * See: https://github.com/vercel/next.js/discussions/40345
 *
 * @example
 * ```ts
 * import { Inter, Roboto_Mono } from 'next/font/google';
 * import { useFontSetup } from '@anchorage/next';
 *
 * const inter = Inter({ subsets: ['latin'], display: 'swap', preload: true, adjustFontFallback: false });
 * const roboto = Roboto_Mono({ subsets: ['latin'], display: 'swap', preload: true, adjustFontFallback: false });
 *
 * function App() {
 *   useFontSetup({
 *     '--font-inter': inter.style.fontFamily,
 *     '--font-roboto-mono': roboto.style.fontFamily,
 *   });
 * }
 * ```
 */
export function useFontSetup(cssVariables: Record<string, string>): void {
  const values = Object.values(cssVariables).join(',');
  useEffect(() => {
    for (const [variable, value] of Object.entries(cssVariables)) {
      document.documentElement.style.setProperty(variable, value);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [values]);
}
